/*
 * @providesModule MatchCellImage
 */
import CurrencyString from 'CurrencyString';
import I18n from 'INTL';
import Sizes from 'Sizes';
import SkillzImage from 'SkillzImage';
import SkillzSVG from 'SkillzSVG';
import SkillzStyle from 'SkillzStyle';
import React from 'react';
import {Platform, StyleSheet, View} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

const {fontSecondary, fontSizeMedium, fontSizeVeryLarge, textColorMatchSelectPrize} =
  SkillzStyle.fullStyles;

// eslint-disable-next-line no-shadow
export enum ETournamentType {
  /** Live event tournament */
  SYNC = 1,
  /** Hosted tournament event */
  EVENT,
  /** Bracket tournament */
  BRACKET,
  /** Head to Head */
  CASH,
  /** VS Friends */
  FRIENDS,
  /** Practice with virtual coin */
  PRACTICE,
}

// eslint-disable-next-line no-shadow
export enum EPrizeType {
  /** Cash prize */
  CASH = 1,
  /** Bonus cash prize */
  BONUS_CASH,
  /** Ticketz to redeem later in the store */
  TICKETZ,
  /** Virtual coin, generaly called Z coin */
  VIRTUAL_COIN,
  /** Custom prize, include physical prize like iPhone/Gift Card/... */
  CUSTOM,
}

interface IMatchCellImageBackgroundProps {
  tournamentType: ETournamentType;
  prizeType: EPrizeType;
}
const PRIZE_BACKGROUND_WIDTH = Sizes.dynamic({
  sm: 80,
  xxlg: 140,
});

const PRIZE_BACKGROUND_HEIGHT = Sizes.dynamic({
  sm: 80,
  xxlg: 125,
});

const styles = StyleSheet.create({
  backgroundContainer: {
    height: PRIZE_BACKGROUND_HEIGHT,
    width: PRIZE_BACKGROUND_WIDTH,
  },
  foregroundContainer: {
    position: 'absolute',
    height: PRIZE_BACKGROUND_HEIGHT,
    width: PRIZE_BACKGROUND_WIDTH,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ticketzImageLarge: {
    width: PRIZE_BACKGROUND_WIDTH * 0.9,
    height: PRIZE_BACKGROUND_HEIGHT * 0.9,
    position: 'absolute',
    backgroundColor: 'transparent',
    alignSelf: 'center',
  },
  syncTemplateIcon: {
    ...Sizes.dynamic({
      sm: {
        height: fontSizeMedium.fontSize,
        width: fontSizeMedium.fontSize,
      },
      xxlg: {
        height: fontSizeVeryLarge.fontSize,
        width: fontSizeVeryLarge.fontSize,
      },
    }),
  },

  prizeText: {
    ...fontSecondary,
    fontSize: Sizes.dynamic({
      sm: fontSizeMedium.fontSize,
      xxlg: fontSizeVeryLarge.fontSize,
    }),
    backgroundColor: 'transparent',
    ...textColorMatchSelectPrize,
    textAlign: 'center',
    alignSelf: 'center',
    ...Platform.select({
      ios: {
        // if you update this, please update prizeTextShadow too
        shadowColor: '#000000',
        shadowOpacity: 0.8,
        shadowRadius: 0,

        shadowOffset: {
          height: 2,
          width: 0,
        },
      },
    }),
  },

  prizeTextShadow: {
    ...Platform.select({
      ios: {
        shadowColor: '#000000',
        shadowOpacity: 0.8,
        shadowRadius: 0,

        shadowOffset: {
          height: 2,
          width: 0,
        },
      },
    }),
  },

  prizeCellPadding: {
    paddingHorizontal: 5,
  },
});

const MatchCellImageBackground = ({tournamentType, prizeType}: IMatchCellImageBackgroundProps) => {
  const gradientColorsForTypes = () => {
    switch (tournamentType) {
      case ETournamentType.EVENT: {
        return SkillzStyle.gradients.prizeViewLiveEventGradient;
      }
      case ETournamentType.BRACKET: {
        switch (prizeType) {
          case EPrizeType.CASH:
          case EPrizeType.BONUS_CASH:
            return SkillzStyle.gradients.bracketPrizeViewCashGradient;
          case EPrizeType.VIRTUAL_COIN:
            return SkillzStyle.gradients.bracketPrizeViewZGradient;
          case EPrizeType.TICKETZ:
            return SkillzStyle.gradients.prizeViewTicketzGradient;
          case EPrizeType.CUSTOM:
          default:
            return SkillzStyle.gradients.bracketPrizeViewLiveEventGradient;
        }
      }
      case ETournamentType.CASH:
      case ETournamentType.FRIENDS:
      case ETournamentType.PRACTICE:
      case ETournamentType.SYNC: {
        switch (prizeType) {
          case EPrizeType.CASH:
          case EPrizeType.BONUS_CASH:
            return SkillzStyle.gradients.prizeViewCashGradient;
          case EPrizeType.VIRTUAL_COIN:
            return SkillzStyle.gradients.prizeViewZGradient;
          case EPrizeType.TICKETZ:
            return SkillzStyle.gradients.prizeViewTicketzGradient;
          default:
            return SkillzStyle.gradients.prizeViewLiveEventGradient;
        }
      }
      default:
        return SkillzStyle.gradients.prizeViewLiveEventGradient;
    }
  };

  const imageForTypes = () => {
    switch (tournamentType) {
      case ETournamentType.SYNC: {
        switch (prizeType) {
          case EPrizeType.CASH:
          case EPrizeType.BONUS_CASH:
            return SkillzStyle.images.prizeViewCashImage;
          case EPrizeType.VIRTUAL_COIN:
            return SkillzStyle.images.prizeViewZImage;
          case EPrizeType.TICKETZ:
            return SkillzStyle.images.prizeViewTicketzImage;
          case EPrizeType.CUSTOM:
          default:
            return SkillzStyle.images.prizeViewSyncImage;
        }
      }
      case ETournamentType.EVENT: {
        return SkillzStyle.images.prizeViewLiveEventImage;
      }
      case ETournamentType.BRACKET: {
        switch (prizeType) {
          case EPrizeType.CASH:
          case EPrizeType.BONUS_CASH:
            return SkillzStyle.images.bracketPrizeViewCashImage;
          case EPrizeType.VIRTUAL_COIN:
            return SkillzStyle.images.bracketPrizeViewZImage;
          case EPrizeType.TICKETZ:
            return SkillzStyle.images.prizeViewTicketzImage;
          case EPrizeType.CUSTOM:
          default:
            return SkillzStyle.images.bracketPrizeViewLiveEventImage;
        }
      }
      case ETournamentType.CASH: {
        switch (prizeType) {
          case EPrizeType.CASH:
          case EPrizeType.BONUS_CASH:
            return SkillzStyle.images.prizeViewCashImage;
          case EPrizeType.VIRTUAL_COIN:
            return SkillzStyle.images.prizeViewZImage;
          case EPrizeType.TICKETZ:
            return SkillzStyle.images.prizeViewTicketzImage;
          default:
            return null;
        }
      }
      case ETournamentType.FRIENDS: {
        switch (prizeType) {
          case EPrizeType.CASH:
          case EPrizeType.BONUS_CASH:
            return SkillzStyle.images.prizeViewCashImage;
          case EPrizeType.VIRTUAL_COIN:
            return SkillzStyle.images.prizeViewZImage;
          case EPrizeType.TICKETZ:
            return SkillzStyle.images.prizeViewTicketzImage;
          case EPrizeType.CUSTOM:
          default:
            return SkillzStyle.images.prizeViewLiveEventImage;
        }
      }
      case ETournamentType.PRACTICE: {
        return SkillzStyle.images.prizeViewZImage;
      }
      default:
        return null;
    }
  };

  const backgroundImage = imageForTypes();
  if (backgroundImage) {
    return <SkillzImage style={[styles.backgroundContainer]} source={backgroundImage} />;
  } else {
    let gradientColors = gradientColorsForTypes() || [];
    gradientColors = gradientColors.map((color: string): string => {
      return SkillzStyle.convertToOpacity(color, 0.95); // eslint-disable-line no-magic-numbers
    });
    return <LinearGradient style={[styles.backgroundContainer]} colors={gradientColors} />;
  }
};

interface IMatchCellImageForegroundProps {
  prizeType: EPrizeType;
  tournamentType: ETournamentType;
  image?: string;
  amount?: number;
  otherStakesCount?: number;
}

const MatchCellImageForeground = ({
  image,
  prizeType,
  tournamentType,
  amount,
  otherStakesCount,
}: IMatchCellImageForegroundProps) => {
  const renderBackImage = () => {
    if (image) {
      return <SkillzImage source={image} style={styles.backgroundContainer} />;
    }
    if (prizeType === EPrizeType.TICKETZ) {
      return (
        <SkillzImage
          source={'ic_ticketz_background.png'}
          resizeMode={'contain'}
          style={styles.ticketzImageLarge}
        />
      );
    }
    return null;
  };

  const renderAmount = () => {
    let prizeString: string = '';
    if (prizeType === EPrizeType.CUSTOM) {
      prizeString = '★';
    } else if (!!amount && (prizeType === EPrizeType.CASH || prizeType === EPrizeType.BONUS_CASH)) {
      prizeString = I18n.getCurrencyString(amount, '$', true, true); //TODO this will need to change for internationalization
    } else if (amount) {
      prizeString = amount.toString();
    } else if (tournamentType === ETournamentType.SYNC && !amount) {
      return (
        <SkillzSVG
          source={'icon_template_sync.svg'}
          style={[styles.syncTemplateIcon, styles.prizeTextShadow]}
          tintColor={SkillzStyle.fullStyles.textColorMatchSelectPrize.color}
        />
      );
    } else {
      return null;
    }
    const threshold = 4; // Threshold overflow value

    let prizeTextStyle = styles.prizeText;
    if (prizeString.length >= threshold && prizeType === EPrizeType.VIRTUAL_COIN) {
      // Set the text small enough to not overflow
      prizeTextStyle = Sizes.dynamic({
        phone: [styles.prizeText, {fontSize: 24}],
        tablet: [styles.prizeText, {fontSize: 44}],
      });
    }
    return (
      <CurrencyString
        amount={prizeString}
        isGradient={false}
        isTicketzTournament={prizeType === EPrizeType.TICKETZ}
        isVirtual={prizeType === EPrizeType.VIRTUAL_COIN}
        style={prizeTextStyle}
        otherStakesCount={otherStakesCount || 0}
      />
    );
  };

  return (
    <View style={styles.foregroundContainer}>
      {renderBackImage()}
      <View style={styles.prizeCellPadding}>{renderAmount()}</View>
    </View>
  );
};

export interface IMatchCellImageProps {
  prizeType: EPrizeType;
  tournamentType: ETournamentType;
  background: string | Omit<IMatchCellImageBackgroundProps, 'prizeType' | 'tournamentType'>;
  foreground?: Omit<IMatchCellImageForegroundProps, 'prizeType' | 'tournamentType'>;
}

const MatchCellImage = ({background, foreground, ...type}: IMatchCellImageProps) => {
  return (
    <View>
      {typeof background === 'string' ? (
        <SkillzImage source={background} style={styles.backgroundContainer} />
      ) : (
        <MatchCellImageBackground {...background} {...type} />
      )}
      {foreground && <MatchCellImageForeground {...foreground} {...type} />}
    </View>
  );
};

export default MatchCellImage;
